<script setup>
import { ref, onMounted, onBeforeUnmount, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import IntegrationEventsAPI from 'dashboard/api/integrationEvents';
import { useShellPeriod } from './composables/useShellPeriod';
import { frontendURL } from 'dashboard/helper/URLHelper';

const store = useStore();
const { t, locale } = useI18n();
// Locale BCP-47 para Intl (o i18n usa '_' tipo pt_BR; Intl quer '-').
const dateLocale = computed(() => (locale.value || 'pt_BR').replace('_', '-'));
function formatEventDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(dateLocale.value);
}
const accountId = computed(() => store.getters.getCurrentAccountId);

const events = ref([]);
const total = ref(0);
const page = ref(1);
const perPage = ref(50);
const loading = ref(false);
const loadError = ref(false);
const summaryLoading = ref(false);
const summaryError = ref(false);
let listRequestId = 0;
let summaryRequestId = 0;
const filters = ref({
  provider: '',
  status: '',
  funnel_id: '',
  from: '',
  to: '',
});
const appliedFilters = ref({ ...filters.value });
// Campos de data nativos (yyyy-mm-dd, com calendário do sistema) e atalhos de
// período como nos outros relatórios; o parser segue tolerante a dd/mm/yyyy.
const dateFrom = ref('');
const dateTo = ref('');
const dateError = ref('');
const activeQuickRange = ref('all');
const QUICK_RANGES = [
  { key: '7', days: 7, label: 'DATE_PICKER.DATE_RANGE_OPTIONS.LAST_7_DAYS' },
  { key: '30', days: 30, label: 'DATE_PICKER.DATE_RANGE_OPTIONS.LAST_30_DAYS' },
  { key: 'all', days: null, label: 'REPORTS.INTEGRATION_EVENTS.ALL_PERIOD' },
];
const ERROR_PREVIEW_LENGTH = 100;

// Parser tolerante: aceita dd/mm/yyyy, dd-mm-yyyy ou yyyy-mm-dd
function parseDate(input) {
  if (!input) return '';
  const s = String(input).trim();
  const isValidCalendarDate = (year, month, day) => {
    const date = new Date(`${year}-${month}-${day}T00:00:00Z`);
    return (
      date.getUTCFullYear() === Number(year) &&
      date.getUTCMonth() + 1 === Number(month) &&
      date.getUTCDate() === Number(day)
    );
  };
  // ISO ja
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) {
    return isValidCalendarDate(m[1], m[2], m[3]) ? s : '';
  }
  // dd/mm/yyyy ou dd-mm-yyyy
  m = s.match(/^(\d{2})[/-](\d{2})[/-](\d{4})$/);
  if (m) {
    const iso = `${m[3]}-${m[2]}-${m[1]}`;
    return isValidCalendarDate(m[3], m[2], m[1]) ? iso : '';
  }
  return ''; // inválido, ignora
}

const summary = ref({});
const funnels = computed(() => store.getters['funnel/getFunnels'] || []);

async function load() {
  listRequestId += 1;
  const requestId = listRequestId;
  const requestedAccountId = accountId.value;
  loading.value = true;
  loadError.value = false;
  try {
    const params = { page: page.value, per_page: perPage.value };
    Object.entries(appliedFilters.value).forEach(([k, v]) => {
      if (v) params[k] = v;
    });
    const { data } = await IntegrationEventsAPI.list(params);
    if (requestId !== listRequestId || requestedAccountId !== accountId.value)
      return;
    events.value = data.data || [];
    total.value = data.meta?.total || 0;
  } catch (e) {
    if (requestId !== listRequestId || requestedAccountId !== accountId.value)
      return;
    loadError.value = true;
  } finally {
    if (requestId === listRequestId) loading.value = false;
  }
}

async function loadSummary() {
  summaryRequestId += 1;
  const requestId = summaryRequestId;
  const requestedAccountId = accountId.value;
  summaryLoading.value = true;
  summaryError.value = false;
  try {
    const params = Object.entries(appliedFilters.value).reduce(
      (result, [key, value]) => {
        if (value) result[key] = value;
        return result;
      },
      {}
    );
    const { data } = await IntegrationEventsAPI.summary(params);
    if (
      requestId !== summaryRequestId ||
      requestedAccountId !== accountId.value
    )
      return;
    summary.value = data;
  } catch (e) {
    if (
      requestId !== summaryRequestId ||
      requestedAccountId !== accountId.value
    )
      return;
    summaryError.value = true;
  } finally {
    if (requestId === summaryRequestId) summaryLoading.value = false;
  }
}

const totalPages = computed(() =>
  Math.max(1, Math.ceil(total.value / perPage.value))
);
const providerLabel = computed(() => ({
  google_ads: t('REPORTS.INTEGRATION_EVENTS.GOOGLE_ADS'),
  meta_capi: t('REPORTS.INTEGRATION_EVENTS.META_ADS'),
}));
const statusLabel = computed(() => ({
  success: t('REPORTS.INTEGRATION_EVENTS.STATUS_OK'),
  error: t('REPORTS.INTEGRATION_EVENTS.STATUS_ERROR'),
  partial_failure: t('REPORTS.INTEGRATION_EVENTS.STATUS_PARTIAL'),
  skipped: t('REPORTS.INTEGRATION_EVENTS.STATUS_SKIPPED'),
}));
// Tokens do design system: contraste legível e troca certa no tema escuro.
const statusBadge = {
  success: 'bg-n-teal-3 text-n-teal-11',
  error: 'bg-n-ruby-3 text-n-ruby-11',
  partial_failure: 'bg-n-amber-3 text-n-amber-11',
  skipped: 'bg-n-slate-3 text-n-slate-11',
};

// Dentro da casa dos relatorios o periodo vem do seletor unico da casca (as
// datas digitadas em dd/mm, aplicadas ao sair do campo, eram o unico jeito
// desta aba e nao acompanhavam a troca de aba). Fora da casa ficam os campos.
const shellPeriod = useShellPeriod(next => {
  filters.value.from = next.startIso;
  filters.value.to = next.endIso;
  appliedFilters.value = { ...filters.value };
  page.value = 1;
  load();
  loadSummary();
});
if (shellPeriod) {
  filters.value.from = shellPeriod.period.value.startIso;
  filters.value.to = shellPeriod.period.value.endIso;
  appliedFilters.value = { ...filters.value };
}

function cardUrl(event) {
  if (!event.kanban_item_id) return '';
  const params = { item: event.kanban_item_id };
  if (event.funnel_id) params.funnel_id = event.funnel_id;
  return frontendURL(`accounts/${accountId.value}/kanban`, params);
}
function conversationLink(event) {
  if (!event.conversation_display_id) return '';
  return frontendURL(
    `accounts/${accountId.value}/conversations/${event.conversation_display_id}`
  );
}
function cardTitle(event) {
  return event.item_title || `#${event.kanban_item_id}`;
}
function isLongError(error) {
  return String(error || '').length > ERROR_PREVIEW_LENGTH;
}
function errorPreview(error) {
  return String(error).slice(0, ERROR_PREVIEW_LENGTH);
}

function applyFilters() {
  if (shellPeriod) {
    appliedFilters.value = { ...filters.value };
    page.value = 1;
    load();
    loadSummary();
    return;
  }
  const parsedFrom = parseDate(dateFrom.value);
  const parsedTo = parseDate(dateTo.value);
  if ((dateFrom.value && !parsedFrom) || (dateTo.value && !parsedTo)) {
    dateError.value = t('REPORTS.INTEGRATION_EVENTS.INVALID_DATE');
    return;
  }
  if (parsedFrom && parsedTo && parsedFrom > parsedTo) {
    dateError.value = t('REPORTS.INTEGRATION_EVENTS.INVALID_RANGE');
    return;
  }
  dateError.value = '';
  filters.value.from = parsedFrom;
  filters.value.to = parsedTo;
  appliedFilters.value = { ...filters.value };
  page.value = 1;
  load();
  loadSummary();
}
function clearDates() {
  dateFrom.value = '';
  dateTo.value = '';
  filters.value.from = '';
  filters.value.to = '';
  dateError.value = '';
  activeQuickRange.value = 'all';
  applyFilters();
}
function toIsoDate(date) {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}
function applyQuickRange(range) {
  activeQuickRange.value = range.key;
  if (!range.days) {
    dateFrom.value = '';
    dateTo.value = '';
  } else {
    const today = new Date();
    const start = new Date(today);
    start.setDate(today.getDate() - (range.days - 1));
    dateFrom.value = toIsoDate(start);
    dateTo.value = toIsoDate(today);
  }
  applyFilters();
}
function onDateChange() {
  activeQuickRange.value = dateFrom.value || dateTo.value ? 'custom' : 'all';
  applyFilters();
}
function nextPage() {
  if (page.value < totalPages.value) {
    page.value += 1;
    load();
  }
}
function prevPage() {
  if (page.value > 1) {
    page.value -= 1;
    load();
  }
}

watch(accountId, () => {
  listRequestId += 1;
  summaryRequestId += 1;
  events.value = [];
  summary.value = {};
  total.value = 0;
  page.value = 1;
  load();
  loadSummary();
});

onMounted(() => {
  store.dispatch('funnel/get');
  load();
  loadSummary();
});
onBeforeUnmount(() => {
  listRequestId += 1;
  summaryRequestId += 1;
});
</script>

<template>
  <!-- A casca dos relatorios ja da a margem lateral (px-4 no celular). Padding
       proprio aqui somava 32px de cada lado e apertava a tabela em 375px. -->
  <div class="mx-auto max-w-screen-xl pt-4 pb-6">
    <header class="mb-4">
      <h2 class="text-lg font-medium text-n-slate-12 mb-1">
        {{ $t('REPORTS.INTEGRATION_EVENTS.TITLE') }}
      </h2>
      <p class="text-sm text-n-slate-11">
        {{ $t('REPORTS.INTEGRATION_EVENTS.SUBTITLE') }}
      </p>
    </header>

    <div
      v-if="loadError"
      role="alert"
      class="mb-4 flex items-center justify-between gap-3 rounded-xl border border-n-weak p-3 text-sm text-n-slate-12"
    >
      <span>{{ $t('REPORTS.INTEGRATION_EVENTS.LOAD_ERROR') }}</span>
      <button
        type="button"
        class="min-h-11 rounded-xl border border-n-slate-4 px-3 hover:bg-n-slate-3"
        @click="load"
      >
        {{ $t('REPORTS.INTEGRATION_EVENTS.RETRY') }}
      </button>
    </div>

    <div
      v-if="summaryError"
      role="alert"
      class="mb-4 flex items-center justify-between gap-3 rounded-xl border border-n-weak p-3 text-sm text-n-slate-12"
    >
      <span>{{ $t('REPORTS.INTEGRATION_EVENTS.SUMMARY_ERROR') }}</span>
      <button
        type="button"
        class="min-h-11 rounded-xl border border-n-slate-4 px-3 hover:bg-n-slate-3"
        :disabled="summaryLoading"
        @click="loadSummary"
      >
        {{ $t('REPORTS.INTEGRATION_EVENTS.RETRY') }}
      </button>
    </div>

    <div v-if="summaryLoading" class="mb-5 text-sm text-n-slate-11">
      {{ $t('REPORTS.INTEGRATION_EVENTS.LOADING') }}
    </div>
    <div
      v-else-if="
        !summaryError && summary && (summary.google_ads || summary.meta_capi)
      "
      class="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-2"
    >
      <div class="min-w-0 rounded-xl bg-n-slate-2 p-3">
        <h4 class="text-xs font-semibold text-n-slate-12 mb-1">
          {{ $t('REPORTS.INTEGRATION_EVENTS.GOOGLE_ADS') }}
        </h4>
        <div class="flex flex-wrap gap-x-3 gap-y-1 text-xs">
          <span class="text-n-teal-11 font-semibold">{{
            `${summary.google_ads?.success || 0} ${$t('REPORTS.INTEGRATION_EVENTS.OK_SHORT')}`
          }}</span>
          <span class="text-n-amber-11 font-semibold">{{
            `${summary.google_ads?.partial_failure || 0} ${$t('REPORTS.INTEGRATION_EVENTS.PARTIAL_SHORT')}`
          }}</span>
          <span class="text-n-ruby-11 font-semibold">{{
            `${summary.google_ads?.error || 0} ${$t('REPORTS.INTEGRATION_EVENTS.ERROR_SHORT')}`
          }}</span>
          <span class="text-n-slate-11 font-semibold">{{
            `${summary.google_ads?.skipped || 0} ${$t('REPORTS.INTEGRATION_EVENTS.SKIPPED_SHORT')}`
          }}</span>
        </div>
      </div>
      <div class="min-w-0 rounded-xl bg-n-slate-2 p-3">
        <h4 class="text-xs font-semibold text-n-slate-12 mb-1">
          {{ $t('REPORTS.INTEGRATION_EVENTS.META_ADS') }}
        </h4>
        <div class="flex flex-wrap gap-x-3 gap-y-1 text-xs">
          <span class="text-n-teal-11 font-semibold">{{
            `${summary.meta_capi?.success || 0} ${$t('REPORTS.INTEGRATION_EVENTS.OK_SHORT')}`
          }}</span>
          <span class="text-n-amber-11 font-semibold">{{
            `${summary.meta_capi?.partial_failure || 0} ${$t('REPORTS.INTEGRATION_EVENTS.PARTIAL_SHORT')}`
          }}</span>
          <span class="text-n-ruby-11 font-semibold">{{
            `${summary.meta_capi?.error || 0} ${$t('REPORTS.INTEGRATION_EVENTS.ERROR_SHORT')}`
          }}</span>
          <span class="text-n-slate-11 font-semibold">{{
            `${summary.meta_capi?.skipped || 0} ${$t('REPORTS.INTEGRATION_EVENTS.SKIPPED_SHORT')}`
          }}</span>
        </div>
      </div>
    </div>

    <div
      class="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2"
      :class="shellPeriod ? 'lg:grid-cols-3' : 'lg:grid-cols-5'"
    >
      <label for="integration-events-provider" class="flex flex-col gap-1">
        <span class="text-xs font-medium text-n-slate-11">{{
          $t('REPORTS.INTEGRATION_EVENTS.FILTER_PROVIDER')
        }}</span>
        <select
          id="integration-events-provider"
          v-model="filters.provider"
          :aria-label="$t('REPORTS.INTEGRATION_EVENTS.FILTER_PROVIDER')"
          class="min-h-11 w-full min-w-0 rounded-xl border border-n-slate-4 bg-n-background px-3 py-2 text-sm text-n-slate-12"
          @change="applyFilters"
        >
          <option value="">
            {{ $t('REPORTS.INTEGRATION_EVENTS.ALL_PROVIDERS') }}
          </option>
          <option value="google_ads">
            {{ $t('REPORTS.INTEGRATION_EVENTS.GOOGLE_ADS') }}
          </option>
          <option value="meta_capi">
            {{ $t('REPORTS.INTEGRATION_EVENTS.META_ADS') }}
          </option>
        </select>
      </label>
      <label for="integration-events-status" class="flex flex-col gap-1">
        <span class="text-xs font-medium text-n-slate-11">{{
          $t('REPORTS.INTEGRATION_EVENTS.FILTER_STATUS')
        }}</span>
        <select
          id="integration-events-status"
          v-model="filters.status"
          :aria-label="$t('REPORTS.INTEGRATION_EVENTS.FILTER_STATUS')"
          class="min-h-11 w-full min-w-0 rounded-xl border border-n-slate-4 bg-n-background px-3 py-2 text-sm text-n-slate-12"
          @change="applyFilters"
        >
          <option value="">
            {{ $t('REPORTS.INTEGRATION_EVENTS.ALL_STATUSES') }}
          </option>
          <option value="success">
            {{ $t('REPORTS.INTEGRATION_EVENTS.STATUS_OK') }}
          </option>
          <option value="partial_failure">
            {{ $t('REPORTS.INTEGRATION_EVENTS.STATUS_PARTIAL') }}
          </option>
          <option value="error">
            {{ $t('REPORTS.INTEGRATION_EVENTS.STATUS_ERROR') }}
          </option>
          <option value="skipped">
            {{ $t('REPORTS.INTEGRATION_EVENTS.STATUS_SKIPPED') }}
          </option>
        </select>
      </label>
      <label for="integration-events-funnel" class="flex flex-col gap-1">
        <span class="text-xs font-medium text-n-slate-11">{{
          $t('REPORTS.INTEGRATION_EVENTS.FILTER_FUNNEL')
        }}</span>
        <select
          id="integration-events-funnel"
          v-model="filters.funnel_id"
          :aria-label="$t('REPORTS.INTEGRATION_EVENTS.FILTER_FUNNEL')"
          class="min-h-11 w-full min-w-0 rounded-xl border border-n-slate-4 bg-n-background px-3 py-2 text-sm text-n-slate-12"
          @change="applyFilters"
        >
          <option value="">
            {{ $t('REPORTS.INTEGRATION_EVENTS.ALL_FUNNELS') }}
          </option>
          <option v-for="f in funnels" :key="f.id" :value="f.id">
            {{ f.name }}
          </option>
        </select>
      </label>
      <label
        v-if="!shellPeriod"
        for="integration-events-date-from"
        class="flex flex-col gap-1"
      >
        <span class="text-xs font-medium text-n-slate-11">{{
          $t('REPORTS.INTEGRATION_EVENTS.FILTER_DATE_FROM')
        }}</span>
        <input
          id="integration-events-date-from"
          v-model="dateFrom"
          type="date"
          :aria-label="$t('REPORTS.INTEGRATION_EVENTS.FILTER_DATE_FROM')"
          :aria-invalid="!!dateError"
          :aria-describedby="
            dateError ? 'integration-events-date-error' : undefined
          "
          class="min-h-11 w-full min-w-0 rounded-xl border border-n-slate-4 bg-n-background px-3 py-2 text-sm text-n-slate-12"
          @change="onDateChange"
        />
      </label>
      <label
        v-if="!shellPeriod"
        for="integration-events-date-to"
        class="flex flex-col gap-1"
      >
        <span class="text-xs font-medium text-n-slate-11">{{
          $t('REPORTS.INTEGRATION_EVENTS.FILTER_DATE_TO')
        }}</span>
        <input
          id="integration-events-date-to"
          v-model="dateTo"
          type="date"
          :aria-label="$t('REPORTS.INTEGRATION_EVENTS.FILTER_DATE_TO')"
          :aria-invalid="!!dateError"
          :aria-describedby="
            dateError ? 'integration-events-date-error' : undefined
          "
          class="min-h-11 w-full min-w-0 rounded-xl border border-n-slate-4 bg-n-background px-3 py-2 text-sm text-n-slate-12"
          @change="onDateChange"
        />
      </label>
      <p
        v-if="dateError"
        id="integration-events-date-error"
        role="alert"
        class="col-span-full text-xs text-n-ruby-11"
      >
        {{ dateError }}
      </p>
      <div
        v-if="!shellPeriod"
        role="group"
        :aria-label="$t('REPORTS.INTEGRATION_EVENTS.QUICK_RANGES')"
        class="col-span-full flex flex-wrap items-center gap-2"
      >
        <button
          v-for="range in QUICK_RANGES"
          :key="range.key"
          type="button"
          class="min-h-11 rounded-xl border px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
          :class="
            activeQuickRange === range.key
              ? 'border-n-brand bg-n-brand/10 text-n-slate-12'
              : 'border-n-slate-4 text-n-slate-11 hover:bg-n-slate-3'
          "
          :aria-pressed="activeQuickRange === range.key"
          :data-testid="`integration-events-range-${range.key}`"
          @click="applyQuickRange(range)"
        >
          {{ $t(range.label) }}
        </button>
        <button
          v-if="dateFrom || dateTo"
          type="button"
          class="min-h-11 rounded-xl px-3 text-sm text-n-slate-11 hover:text-n-slate-12"
          @click="clearDates"
        >
          {{ $t('REPORTS.INTEGRATION_EVENTS.CLEAR') }}
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-sm text-n-slate-11">
      {{ $t('REPORTS.INTEGRATION_EVENTS.LOADING') }}
    </div>

    <div
      v-else-if="events.length"
      class="overflow-x-auto rounded-xl border border-n-slate-4"
    >
      <table class="w-full text-xs">
        <thead class="bg-n-slate-2 text-n-slate-11">
          <tr>
            <th class="px-3 py-2 text-left font-semibold">
              {{ $t('REPORTS.INTEGRATION_EVENTS.COL_WHEN') }}
            </th>
            <th class="px-3 py-2 text-left font-semibold">
              {{ $t('REPORTS.INTEGRATION_EVENTS.COL_PROVIDER') }}
            </th>
            <th class="px-3 py-2 text-left font-semibold">
              {{ $t('REPORTS.INTEGRATION_EVENTS.COL_STATUS') }}
            </th>
            <th class="px-3 py-2 text-left font-semibold">
              {{ $t('REPORTS.INTEGRATION_EVENTS.COL_CARD') }}
            </th>
            <th class="px-3 py-2 text-left font-semibold">
              {{ $t('REPORTS.INTEGRATION_EVENTS.COL_STAGE') }}
            </th>
            <th class="px-3 py-2 text-left font-semibold">
              {{ $t('REPORTS.INTEGRATION_EVENTS.COL_HTTP') }}
            </th>
            <th class="px-3 py-2 text-left font-semibold">
              {{ $t('REPORTS.INTEGRATION_EVENTS.COL_DETAIL') }}
            </th>
          </tr>
        </thead>
        <tbody class="text-n-slate-12">
          <tr
            v-for="(e, idx) in events"
            :key="idx"
            class="border-t border-n-slate-4"
          >
            <td class="px-3 py-2 align-top">
              {{ formatEventDate(e.occurred_at) }}
            </td>
            <td class="px-3 py-2 align-top">
              {{ providerLabel[e.provider] || e.provider }}
            </td>
            <td class="px-3 py-2 align-top">
              <span
                class="px-2 py-0.5 rounded text-[10px] font-semibold"
                :class="statusBadge[e.status] || statusBadge.skipped"
              >
                {{ statusLabel[e.status] || e.status }}
              </span>
            </td>
            <td class="px-3 py-2 align-top">
              <div class="flex items-center gap-1">
                <a
                  v-if="cardUrl(e)"
                  :href="cardUrl(e)"
                  class="inline-flex min-h-11 items-center text-n-blue-11 underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand lg:min-h-0"
                  :aria-label="
                    $t('REPORTS.INTEGRATION_EVENTS.OPEN_CARD', {
                      title: cardTitle(e),
                    })
                  "
                  data-testid="integration-event-card-link"
                >
                  {{ cardTitle(e) }}
                </a>
                <span v-else>{{ cardTitle(e) }}</span>
                <a
                  v-if="conversationLink(e)"
                  v-tooltip.top="
                    $t('REPORTS.INTEGRATION_EVENTS.OPEN_CONVERSATION', {
                      id: e.conversation_display_id,
                    })
                  "
                  :href="conversationLink(e)"
                  class="inline-flex size-11 shrink-0 items-center justify-center rounded-lg text-n-slate-11 hover:bg-n-slate-3 hover:text-n-slate-12 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand lg:size-7"
                  :aria-label="
                    $t('REPORTS.INTEGRATION_EVENTS.OPEN_CONVERSATION', {
                      id: e.conversation_display_id,
                    })
                  "
                  data-testid="integration-event-conversation-link"
                >
                  <span aria-hidden="true" class="i-ph-chat-circle size-4" />
                </a>
              </div>
            </td>
            <td class="px-3 py-2 align-top">
              {{ e.event_label || e.stage_key }}
            </td>
            <td class="px-3 py-2 align-top">{{ e.http_status || '-' }}</td>
            <td
              class="px-3 py-2 align-top text-n-slate-11 font-mono text-[10px] max-w-[280px] break-all"
            >
              <details v-if="isLongError(e.error)" class="group">
                <summary class="cursor-pointer list-none">
                  <span class="group-open:hidden">
                    {{ errorPreview(e.error) }}
                  </span>
                  <span
                    class="font-sans text-n-blue-11 underline-offset-2 hover:underline"
                  >
                    {{ $t('REPORTS.INTEGRATION_EVENTS.SHOW_FULL_ERROR') }}
                  </span>
                </summary>
                <p class="mt-1 whitespace-pre-wrap">{{ e.error }}</p>
              </details>
              <template v-else>{{ e.error || '' }}</template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div
      v-else-if="!loadError"
      class="p-12 text-center bg-n-slate-2 rounded-xl text-n-slate-11 text-sm"
    >
      {{ $t('REPORTS.INTEGRATION_EVENTS.EMPTY') }}
    </div>

    <footer
      class="mt-4 flex justify-between items-center text-xs text-n-slate-11"
    >
      <button
        type="button"
        class="inline-flex min-h-11 items-center gap-1 rounded border border-n-slate-4 px-3 py-1 hover:bg-n-slate-3 disabled:opacity-40"
        :disabled="page === 1"
        @click="prevPage"
      >
        <span aria-hidden="true" class="i-ph-arrow-left" />
        {{ $t('REPORTS.INTEGRATION_EVENTS.PREVIOUS') }}
      </button>
      <span>{{
        $t('REPORTS.INTEGRATION_EVENTS.PAGINATION', { page, totalPages, total })
      }}</span>
      <button
        type="button"
        class="inline-flex min-h-11 items-center gap-1 rounded border border-n-slate-4 px-3 py-1 hover:bg-n-slate-3 disabled:opacity-40"
        :disabled="page >= totalPages"
        @click="nextPage"
      >
        {{ $t('REPORTS.INTEGRATION_EVENTS.NEXT') }}
        <span aria-hidden="true" class="i-ph-arrow-right" />
      </button>
    </footer>
  </div>
</template>
